/* Collection / search filter panel.

   Two custom elements:
     <filters-drawer>  the slide-in panel, its scrim, and the open/close plumbing
     <filter-group>    one collapsible group inside it

   <filter-group> deliberately does not reuse the shared <accordion-wrapper>. That
   component closes an open item on any click landing outside it, which fights a panel
   where several groups are meant to stay open while you tick boxes in another. */

class FiltersDrawer extends HTMLElement {
  constructor() {
    super();

    this.trigger = document.querySelector(".js-drawer-trigger");
    this.closers = this.querySelectorAll(".js-drawer-close");
    this.form = this.closest("form");
    this.count = document.querySelector(".filters__button-count");
    this.refinements = this.querySelector(".filters__refinements");
    this.lastFocused = null;

    this.onKeydown = this.onKeydown.bind(this);

    this.addListeners();
  }

  addListeners() {
    if (this.trigger) this.trigger.addEventListener("click", () => this.toggle());

    this.closers.forEach((el) => el.addEventListener("click", () => this.close()));

    // facets.js re-renders the grid and the facet lists but never the toolbar, so the
    // badge on the closed panel has to be kept in step from here. Ticking a box updates it
    // straight away rather than waiting on the round trip.
    if (this.form) {
      this.form.addEventListener("change", (event) => {
        this.syncSizeBand(event.target);
        this.enforceSingleSelect(event.target);
        this.updateCount();
      });
    }

    // Removing a filter by its chip, or via Clear all, never fires a change event — it
    // goes through facet-remove and re-renders. That re-render replaces the option lists
    // inside .js-filter blocks, but not the Available Now switch (which sits outside one)
    // and not the toolbar. So re-sync the whole panel from the URL whenever the chip row
    // changes, which is the one thing every one of those paths does update.
    if (this.refinements) {
      new MutationObserver(() => {
        this.syncFromUrl();
        this.updateEmptyGroups();
      }).observe(this.refinements, { childList: true });
    }
  }

  /* Liquid decides this on first paint; this keeps it right afterwards. facets.js swaps the
     options inside each .js-filter on every re-render but never touches the group wrapper,
     and it renders active facets last — so by the time the chip row mutates, the new option
     lists are already in place and safe to measure.

     Groups without a .filters__list (the price range) are left alone: they have no options
     to count and should always stay available. */
  updateEmptyGroups() {
    this.querySelectorAll("filter-group.js-filter").forEach((group) => {
      // Hidden for this collection by custom.hidden_filters — Liquid's decision, not ours.
      if (group.dataset.hiddenByCollection === "true") return;

      const list = group.querySelector(".filters__list");
      if (!list) return;

      const usable = list.querySelectorAll("input:not([disabled])").length;
      group.toggleAttribute("hidden", usable === 0);
    });
  }

  /* Groups marked data-single-select hold one value at a time: ticking a second releases the
     first. Kept as checkboxes rather than radios on purpose — a radio cannot be unticked, so
     switching to them would leave no way to clear the colour from the group itself.

     Runs on `change`, which is synchronous, while facets.js submits from a 500ms debounced
     `input` handler that reads FormData when it finally fires. So the released value is
     already unchecked by the time the request is built. */
  enforceSingleSelect(changed) {
    if (!changed || changed.type !== "checkbox" || !changed.checked) return;

    const group = changed.closest('filter-group[data-single-select="true"]');
    if (!group) return;

    group.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      if (input !== changed) input.checked = false;
    });
  }

  /* A size band (snippets/product-filters.liquid) is one tick box standing for every size
     whose name starts with it. The box has no name, so it posts nothing: it switches its
     sizes' hidden inputs on and off, and disabled inputs are left out of the FormData that
     facets.js builds. Same synchronous-change timing as enforceSingleSelect above. */
  syncSizeBand(changed) {
    if (!changed || changed.dataset.sizeBand === undefined) return;

    this.setSizeBandMembers(changed);
  }

  setSizeBandMembers(box) {
    box
      .closest(".filters__item")
      .querySelectorAll("input[data-size-band-member]")
      .forEach((input) => {
        input.disabled = !box.checked;
      });
  }

  syncFromUrl() {
    if (!this.form) return;

    const active = new Set();

    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key.startsWith("filter.")) active.add(`${key}=${value}`);
    });

    this.form
      .querySelectorAll('input[name^="filter."][type="checkbox"]')
      .forEach((input) => {
        input.checked = active.has(`${input.name}=${input.value}`);
      });

    // A band is ticked while any of its sizes is in the URL.
    this.form.querySelectorAll("input[data-size-band]").forEach((box) => {
      const members = box.closest(".filters__item").querySelectorAll("input[data-size-band-member]");

      box.checked = [...members].some((input) => active.has(`${input.name}=${input.value}`));
      this.setSizeBandMembers(box);
    });

    this.updateCount();
  }

  updateCount() {
    if (!this.count || !this.form) return;

    // A band box has no name, so it is counted separately — once, like the chip it makes.
    const checked = this.form.querySelectorAll(
      'input[name^="filter."]:checked, input[data-size-band]:checked'
    ).length;
    const prices = [...this.form.querySelectorAll('input[name*=".price."]')].filter(
      (input) => input.value !== ""
    ).length;

    const total = checked + (prices > 0 ? 1 : 0);

    this.count.textContent = total;
    this.count.toggleAttribute("hidden", total === 0);
  }

  get isOpen() {
    return this.classList.contains("filters-drawer--open");
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.lastFocused = document.activeElement;

    // Every opening starts from the same place: all groups shut. Collapsed without
    // animating, so they are already closed as the panel slides in rather than folding
    // up in front of the user. Applied filters stay visible as chips at the top.
    this.querySelectorAll("filter-group").forEach((group) => group.collapse());

    this.classList.add("filters-drawer--open");
    document.body.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.onKeydown);

    if (this.trigger) {
      this.trigger.classList.add("active");
      this.trigger.setAttribute("aria-expanded", "true");
    }

    const close = this.querySelector(".filters__close");
    if (close) close.focus();
  }

  close() {
    this.classList.remove("filters-drawer--open");
    document.body.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this.onKeydown);

    if (this.trigger) {
      this.trigger.classList.remove("active");
      this.trigger.setAttribute("aria-expanded", "false");
    }

    if (this.lastFocused) this.lastFocused.focus();
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close();
  }
}

customElements.define("filters-drawer", FiltersDrawer);

class FilterGroup extends HTMLElement {
  constructor() {
    super();

    this.header = this.querySelector(".filters__group-header");
    this.content = this.querySelector(".filters__group-content");

    if (!this.header || !this.content) return;

    this.header.addEventListener("click", () => this.toggle());

    // Groups start collapsed; set data-open="true" on the element to start expanded.
    this.setState(this.dataset.open === "true", false);
  }

  get isOpen() {
    return this.classList.contains("filters__group-item--open");
  }

  toggle() {
    this.setState(!this.isOpen, true);
  }

  collapse() {
    this.setState(false, false);
  }

  setState(open, animate) {
    this.classList.toggle("filters__group-item--open", open);
    this.header.setAttribute("aria-expanded", open ? "true" : "false");

    if (!animate) {
      this.content.style.height = open ? "auto" : "0px";
      return;
    }

    // Animate from a measured pixel height, then hand back to `auto` so the group keeps
    // fitting its contents when facets.js swaps the option list underneath it.
    if (open) {
      const target = `${this.content.scrollHeight}px`;

      this.content.style.height = "0px";
      getComputedStyle(this.content).height;

      requestAnimationFrame(() => {
        this.content.style.height = target;
      });

      const onDone = () => {
        if (this.isOpen) this.content.style.height = "auto";
        this.content.removeEventListener("transitionend", onDone);
      };

      this.content.addEventListener("transitionend", onDone);
      return;
    }

    this.content.style.height = `${this.content.scrollHeight}px`;
    getComputedStyle(this.content).height;

    requestAnimationFrame(() => {
      this.content.style.height = "0px";
    });
  }
}

customElements.define("filter-group", FilterGroup);
